"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { CloudArrowUp } from "@phosphor-icons/react";

import { Link } from "@/navigation";

const NAMESPACE = "new-3d-printing";

const IMAGE_BASE = "/image/new-version/more/3D-Printing/Materials-for-Plastic-&-Metal-Parts";

type MaterialCategory = "metal" | "plastic";

interface MaterialItem {
	key: string;
	category: MaterialCategory;
	images: string[];
	colors: string[];
}

const COLOR_SWATCH_MAP: Record<string, string> = {
	silver: "#c0c0c0",
	yellow: "#e8c547",
	black: "#1f1f1f",
	"creamy white": "#f3ead3",
	white: "#f5f5f5",
};

const MATERIALS: readonly MaterialItem[] = [
	{
		key: "AluminiumAlloy",
		category: "metal",
		images: [
			"aluminium-alloy-1.webp",
			"aluminium-alloy-2.webp",
			"aluminium-alloy-3.webp",
			"aluminium-alloy-4.webp",
		],
		colors: ["silver"],
	},
	{
		key: "StainlessSteel",
		category: "metal",
		images: [
			"stainless-steel-1.webp",
			"stainless-steel-2.webp",
			"stainless-steel-3.webp",
			"stainless-steel-4.webp",
		],
		colors: ["silver"],
	},
	{
		key: "ABS",
		category: "plastic",
		images: ["abs-1.webp", "abs-2.webp", "abs-3.webp", "abs-4.webp"],
		colors: ["yellow", "black", "creamy white"],
	},
	{
		key: "PANylon",
		category: "plastic",
		images: ["pa-nylon-1.webp", "pa-nylon-2.webp", "pa-nylon-3.webp", "pa-nylon-4.webp"],
		colors: ["black", "white"],
	},
] as const;

export default function Materials() {
	const t = useTranslations(NAMESPACE);
	const [activeIndex, setActiveIndex] = useState(0);
	const [activeImageIndex, setActiveImageIndex] = useState(0);

	const handleMaterialChange = (index: number) => {
		setActiveIndex(index);
		setActiveImageIndex(0);
	};

	const active = MATERIALS[activeIndex];
	const name = t(`material${active.key}Name` as any);
	const desc = t(`material${active.key}Desc` as any);
	const color = t(`material${active.key}Color` as any);
	const subtypes = t(`material${active.key}Subtypes` as any);
	const price = t(`material${active.key}Price` as any);
	const leadTime = t(`material${active.key}LeadTime` as any);
	const alt = t(`material${active.key}Alt` as any);

	const metals = MATERIALS.filter((m) => m.category === "metal");
	const plastics = MATERIALS.filter((m) => m.category === "plastic");

	return (
		<section className="bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("materialsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("materialsDesc")}</p>
				</header>

				<div className="mx-auto flex flex-col gap-0 bg-white lg:flex-row">
					{/* 左侧分类列表 */}
					<div className="w-full shrink-0 bg-[#f1f1f1] px-4 lg:w-[300px]">
						<div className="border-b border-[#cbcbcb]">
							<h3 className="mb-2 ml-2 mt-4 w-fit rounded-full bg-white px-3 py-1 text-sm font-bold uppercase tracking-wider text-black">
								{t("materialsMetalsTitle")}
							</h3>
							<div className="flex flex-wrap gap-1 pb-2 lg:flex-col lg:gap-0">
								{metals.map((material, index) => (
									<button
										key={material.key}
										onClick={() => handleMaterialChange(index)}
										className={`w-full px-5 py-3 text-left text-sm font-medium transition-colors ${
											activeIndex === index
												? "bg-white text-themePrimary400"
												: "text-gray-600 hover:bg-white hover:text-themePrimary400"
										}`}
									>
										{t(`material${material.key}Name` as any)}
									</button>
								))}
							</div>
						</div>

						<div>
							<h3 className="mb-2 ml-2 mt-4 w-fit rounded-full bg-white px-3 py-1 text-sm font-bold uppercase tracking-wider text-black">
								{t("materialsPlasticsTitle")}
							</h3>
							<div className="flex flex-wrap gap-1 pb-4 lg:flex-col lg:gap-0">
								{plastics.map((material, idx) => {
									const globalIndex = metals.length + idx;
									return (
										<button
											key={material.key}
											onClick={() => handleMaterialChange(globalIndex)}
											className={`w-full px-5 py-2.5 text-left text-sm font-medium transition-colors ${
												activeIndex === globalIndex
													? "bg-white text-themePrimary400"
													: "text-gray-600 hover:bg-white hover:text-themePrimary400"
											}`}
										>
											{t(`material${material.key}Name` as any)}
										</button>
									);
								})}
							</div>
						</div>
					</div>

					{/* 右侧内容区 */}
					<div className="flex-1 p-6 lg:p-8">
						<div className="mb-6">
							<span className="inline-block rounded bg-themePrimary400 px-4 py-2.5 text-sm font-semibold text-white">
								{name}
							</span>
						</div>

						<div className="flex flex-col gap-8 border-t border-[#cbcbcb] max-md:gap-4 lg:flex-row lg:gap-0">
							{/* 左侧图片 */}
							<div className="w-full border-b border-[#cbcbcb] p-6 pb-6 max-md:p-4 lg:w-1/2 lg:border-b-0 lg:border-r">
								<div className="relative mb-4 aspect-[4/3] w-full overflow-hidden bg-[#bfbfbf]">
									<Image
										key={`${active.key}-${activeImageIndex}`}
										src={`${IMAGE_BASE}/${active.images[activeImageIndex]}`}
										alt={alt}
										fill
										priority
										className="object-contain p-4"
										sizes="(max-width: 768px) 100vw, 400px"
									/>
								</div>
								<div className="grid grid-cols-3 gap-3">
									{active.images.slice(0, 3).map((image, index) => (
										<button
											key={`${active.key}-thumb-${index}`}
											onClick={() => setActiveImageIndex(index)}
											className={`relative aspect-[4/3] w-full overflow-hidden border-2 bg-[#e5e5e5] transition-all ${
												activeImageIndex === index
													? "border-themePrimary400"
													: "border-gray-200 hover:border-gray-400"
											}`}
										>
											<Image
												src={`${IMAGE_BASE}/${image}`}
												alt={`${alt} ${index + 1}`}
												fill
												className="object-contain p-2"
												sizes="(max-width: 768px) 30vw, 120px"
											/>
										</button>
									))}
								</div>
								{active.images[3] && (
									<div className="mt-3 grid grid-cols-3 gap-3">
										<button
											onClick={() => setActiveImageIndex(3)}
											className={`relative aspect-[4/3] w-full overflow-hidden border-2 bg-[#e5e5e5] transition-all ${
												activeImageIndex === 3
													? "border-themePrimary400"
													: "border-gray-200 hover:border-gray-400"
											}`}
										>
											<Image
												src={`${IMAGE_BASE}/${active.images[3]}`}
												alt={`${alt} 4`}
												fill
												className="object-contain p-2"
												sizes="(max-width: 768px) 30vw, 120px"
											/>
										</button>
									</div>
								)}
							</div>

							{/* 右侧详情 */}
							<div className="w-full p-6 max-md:p-4 lg:w-1/2">
								<h3 className="mb-4 text-lg font-bold text-black sm:text-xl md:text-2xl">{name}</h3>
								<p className="mb-6 text-sm leading-relaxed text-gray-900">{desc}</p>

								<div className="space-y-4">
									<div className="flex items-start gap-4">
										<span className="w-28 shrink-0 text-sm font-semibold text-black">
											{t("materialsLabelColor")}:
										</span>
										<span className="inline-flex flex-wrap items-center gap-2 rounded-full bg-gray-100 py-1.5 pl-2 pr-4 text-sm text-gray-900">
											{active.colors.map((c) => (
												<span
													key={c}
													className="inline-block h-5 w-5 rounded-full border border-gray-200"
													style={{ backgroundColor: COLOR_SWATCH_MAP[c] ?? "#cccccc" }}
												/>
											))}
											<span>{color}</span>
										</span>
									</div>

									<div className="flex items-start gap-4">
										<span className="w-28 shrink-0 text-sm font-semibold text-black">
											{t("materialsLabelSubtypes")}:
										</span>
										<span className="text-sm text-gray-900">{subtypes}</span>
									</div>

									<div className="flex items-start gap-4">
										<span className="w-28 shrink-0 text-sm font-semibold text-black">
											{t("materialsLabelPrice")}:
										</span>
										<span className="text-sm font-medium text-gray-900">{price}</span>
									</div>

									<div className="flex items-start gap-4">
										<span className="w-28 shrink-0 text-sm font-semibold text-black">
											{t("materialsLabelLeadTime")}:
										</span>
										<span className="text-sm text-gray-900">{leadTime}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* 底部 CTA */}
				<div className="mx-auto mt-10 flex w-fit flex-col items-center justify-center gap-4 border border-gray-200 bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.08)] sm:flex-row sm:gap-6 md:mt-12">
					<p className="max-w-[320px] text-base font-bold leading-snug text-black sm:text-lg">
						{t("materialsCtaText")}
					</p>
					<Link
						href="/upload"
						className="inline-flex items-center gap-2 rounded bg-themePrimary400 px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-[#83b751]"
					>
						<span>{t("materialsCtaButton")}</span>
						<CloudArrowUp size={20} weight="bold" />
					</Link>
				</div>
			</div>
		</section>
	);
}
